import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography, Button, TextField, Chip, IconButton, Grid, Paper, Pagination } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import { useDispatch, useSelector } from "react-redux";
import { getAllVillagesAction } from "../../../store/actions";
import AddVillage from "./AddVillage";

const Village = () => {
    const [open, setOpen] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [selectedVillageId, setSelectedVillageId] = useState("");
    const [searchQuery, setSearchQuery] = useState("");
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 30;
    const dispatch = useDispatch();

    // Lists
    const { successData: villagesData, loading: villagesLoading } = useSelector((state) => state.getAllVillagesReducer || {});

    const villages = useMemo(() => {
        if (Array.isArray(villagesData)) return villagesData;
        if (villagesData && villagesData.data && Array.isArray(villagesData.data.villages)) return villagesData.data.villages;
        return [];
    }, [villagesData]);

    // Filter by id/name/mandalId/mandalName/status
    const filteredVillages = useMemo(() => {
        const q = (searchQuery || "").toString().trim().toLowerCase();
        if (!q) return villages;
        return villages.filter((v) => {
            const id = (v.villageId || v.id || v.village_id || "").toString().toLowerCase();
            const name = (v.name || v.village_name || v.villageName || "").toString().toLowerCase();
            const mandalId = (v.mandal_id || v.mandalId || v.mandalID || "").toString().toLowerCase();
            const mandalName = (v.mandal_name || v.mandalName || "").toString().toLowerCase();
            const status = (v.status || "").toString().toLowerCase();
            return id.includes(q) || name.includes(q) || mandalId.includes(q) || mandalName.includes(q) || status.includes(q);
        });
    }, [villages, searchQuery]);

    // Normalize once for clean JSX rendering
    const normalizeVillage = (v) => ({
        id: v.villageId || v.id || v.village_id || "",
        name: v.name || v.village_name || v.villageName || "",
        mandalId: v.mandal_id || v.mandalId || v.mandalID || "",
        mandalName: v.mandal_name || v.mandalName || (v.mandal_id || v.mandalId || v.mandalID || ""),
        status: v.status || "Active",
        raw: v,
    });

    const normalizedVillages = useMemo(() => filteredVillages.map(normalizeVillage), [filteredVillages]);

    // Reset page when search changes
    useEffect(() => { setPage(1); }, [searchQuery]);

    const totalPages = Math.max(1, Math.ceil(normalizedVillages.length / PAGE_SIZE));
    const pagedVillages = normalizedVillages.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

    useEffect(() => {
        dispatch(getAllVillagesAction());
    }, [dispatch]);

    const handleAdd = () => {
        setIsEdit(false);
        setSelectedVillageId("");
        setOpen(true);
    };

    const handleEdit = (village) => {
        const villageId = village.villageId || village.id || village.village_id || village.villageID;
        if (!villageId) return;
        setIsEdit(true);
        setSelectedVillageId(String(villageId));
        setOpen(true);
    };

    const handleCloseDialog = () => {
        setOpen(false);
        setIsEdit(false);
        setSelectedVillageId("");
    };

    const getStatusChipStyles = (status) => {
        const s = (status || '').toString().toLowerCase();
        if (s === 'active') return { backgroundColor: '#D1FAE5', color: '#047857' };
        if (s === 'inactive') return { backgroundColor: '#FECACA', color: '#B91C1C' };
        if (s === 'pending') return { backgroundColor: '#FDE68A', color: '#92400E' };
        return { backgroundColor: '#E5E7EB', color: '#111827' };
    };

    return (
        <Box sx={{ minHeight: '100vh', mt: 2 }}>
            <Typography fontSize={18} fontWeight={600} color="#111827" mb={2}>
                Village Management
            </Typography>

            <Box
                sx={{
                    display: "flex",
                    gap: 2,
                    flexWrap: "wrap",
                    justifyContent: "space-between",
                    mb: 3,
                    // width: { xs: 330, sm: 'auto' },
                    p: 2,
                    backgroundColor: '#fff',
                    borderRadius: 3,
                    boxShadow: "0px 4px 12px rgba(0,0,0,0.08)",
                }}
            >
                <TextField
                    placeholder="Search ..."
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{
                        width: { xs: 300, sm: 300 },
                        '& .MuiOutlinedInput-root': {
                            borderRadius: 3,
                        },
                        '& .MuiInputBase-input': {
                            borderRadius: 3,
                        },
                    }}
                />

                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    sx={{
                        bgcolor: "#4F46E5",
                        textTransform: "none",
                        borderRadius: 3,
                        width:{xs:300},
                        px: 3,
                    }}
                    onClick={handleAdd}
                >
                    Add New Village
                </Button>
            </Box>

            {/* Table for sm+ screens - Zone-style grid */}
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
                {/* Header */}
                <Box
                    display={{ xs: 'none', sm: 'grid' }}
                    gridTemplateColumns={{ sm: '1fr 1.6fr 1fr 1fr 1fr' }}
                    alignItems="center"
                    px={{ xs: 1, sm: 2 }}
                    py={{ xs: 1, sm: 2 }}
                    mb={{ xs: 1, sm: 1 }}
                >
                    {['VILLAGE ID', 'VILLAGE NAME', 'MANDAL', 'STATUS', 'ACTIONS'].map((label, i) => (
                        <Typography
                            key={label}
                            fontSize={{ sm: 10, md: 12 }}
                            color="#000000"
                            fontWeight={700}
                            sx={{
                                textTransform: 'uppercase',
                                textAlign: i === 3 ? 'center' : i === 4 ? 'right' : 'left',
                            }}
                        >
                            {label}
                        </Typography>
                    ))}
                </Box>

                {/* Rows */}
                {villagesLoading && (
                    <Paper elevation={0} sx={{ borderRadius: 3, border: '1px solid #E5E7EB', px: 2, py: 1.5, mb: 2 }}>
                        <Typography variant="body2">Loading villages...</Typography>
                    </Paper>
                )}
                {!villagesLoading && pagedVillages.map(({ id: villageId, name, mandalName, status, raw }) => {
                    const chipSx = {
                        width: 90,
                        height: 24,
                        ...getStatusChipStyles(status),
                        '& .MuiChip-label': { fontSize: 12, fontWeight: 600 },
                    };
                    return (
                        <Paper
                            key={villageId || name}
                            elevation={0}
                            sx={{
                                borderRadius: 3,
                                display: { xs: 'none', sm: 'grid' },
                                gridTemplateColumns: { sm: '1fr 1.6fr 1fr 1fr 1fr' },
                                alignItems: 'center',
                                border: '1px solid #E5E7EB',
                                px: 2,
                                py: 1.5,
                                gap: 0,
                                columnGap: 0,
                                mb: 1.25,
                                '&:hover': {
                                    border: '2px solid #2563EB',
                                    boxShadow: '0 4px 14px rgba(37,99,235,0.18)',
                                },
                            }}
                        >
                            <Typography variant="body2" sx={{ textAlign: 'left' }}>{villageId}</Typography>
                            <Typography variant="body2" sx={{ textAlign: 'left' }}>{name}</Typography>
                            <Typography variant="body2" sx={{ textAlign: 'left' }}>{mandalName}</Typography>
                            <Box sx={{ justifySelf: 'center' }}>
                                <Chip label={String(status).charAt(0).toUpperCase() + String(status).slice(1)} size="small" sx={chipSx} />
                            </Box>
                            <Box display="flex" alignItems="center" gap={1} justifyContent="flex-end" sx={{ justifySelf: 'end' }}>
                                <IconButton size="small" aria-label="edit village" onClick={() => handleEdit(raw)}>
                                    <EditOutlinedIcon fontSize="small" sx={{ color: '#2563EB', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.25))' }} />
                                </IconButton>
                            </Box>
                        </Paper>
                    );
                })}
                {!villagesLoading && normalizedVillages.length === 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>No villages found.</Typography>
                )}
                {!villagesLoading && totalPages > 1 && (
                    <Box display="flex" alignItems="center" justifyContent="space-between" mt={2} flexWrap="wrap" gap={1}>
                        <Typography variant="body2">
                            Showing {Math.min((page - 1) * PAGE_SIZE + 1, normalizedVillages.length)} to {Math.min(page * PAGE_SIZE, normalizedVillages.length)} of {normalizedVillages.length} entries
                        </Typography>
                        <Pagination count={totalPages} page={page} onChange={(_, val) => setPage(val)} color="primary" />
                    </Box>
                )}
            </Box>

            {/* Cards for xs screens */}
            <Grid container spacing={2} sx={{ display: { xs: 'flex', sm: 'none' } }} justifyContent="center">
                {!villagesLoading && pagedVillages.map(({ id: villageId, name, mandalName, status, raw }) => {
                    return (
                        <Grid item xs={12} key={villageId || name} sx={{ display: 'flex', justifyContent: 'center' }}>
                            <Paper
                                elevation={0}
                                sx={{
                                    p: 2,
                                    borderRadius: 3,
                                    width: 300,
                                    backgroundColor: '#fff',
                                    border: '1px solid #E5E7EB',
                                    boxShadow: '0 4px 14px rgba(37,99,235,0.18)',
                                    '&:hover': {
                                        border: '2px solid #2563EB',
                                        boxShadow: '0 4px 14px rgba(37,99,235,0.18)',
                                    },
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 4 }}>
                                    <Box />
                                    <Chip
                                        label={String(status).charAt(0).toUpperCase() + String(status).slice(1)}
                                        size="small"
                                        sx={{ ...getStatusChipStyles(status), fontWeight: 600 }}
                                    />
                                </Box>
                                <Box sx={{ mb: 1 }}>
                                    <Typography variant="body2" fontWeight={600} color="#0F172A">Village: {name}</Typography>
                                    <Typography variant="body2" fontWeight={600} color="#64748B">Village ID: {villageId}</Typography>
                                    <Typography variant="body2" fontWeight={600} color="#64748B">Mandal: {mandalName}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                                    <IconButton size="small" aria-label="view village">
                                        <VisibilityOutlinedIcon fontSize="small" sx={{ color: '#046645' }} />
                                    </IconButton>
                                    <IconButton size="small" aria-label="edit village" onClick={() => handleEdit(raw)}>
                                        <EditOutlinedIcon fontSize="small" sx={{ color: '#2563EB' }} />
                                    </IconButton>
                                </Box>
                            </Paper>
                        </Grid>
                    );
                })}
                {!villagesLoading && normalizedVillages.length === 0 && (
                    <Grid item xs={12}>
                        <Typography variant="body2" color="text.secondary">No villages found.</Typography>
                    </Grid>
                )}
                {!villagesLoading && totalPages > 1 && (
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mt={1} flexWrap="wrap" gap={1}>
                            <Typography variant="body2" >
                                Showing {Math.min((page - 1) * PAGE_SIZE + 1, normalizedVillages.length)} to {Math.min(page * PAGE_SIZE, normalizedVillages.length)} of {normalizedVillages.length} entries
                            </Typography>
                            <Pagination count={totalPages} page={page} onChange={(_, val) => setPage(val)} color="primary" size="small" />
                        </Box>
                    </Grid>
                )}
            </Grid>

            <AddVillage
                open={open}
                onClose={handleCloseDialog}
                isEdit={isEdit}
                villageId={selectedVillageId}
                onSuccess={() => dispatch(getAllVillagesAction())}
            />
        </Box>
    );
};

export default Village;
